import React, { FC, useEffect } from 'react';
import {
    TextField, FormControl,
    InputLabel, Select, MenuItem,
    FormControlLabel, Checkbox,
    Radio, RadioGroup, Grid,
    Typography, Box, IconButton, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { GrantConcessionEnum } from 'repository/timekeeper/@types/TypeGrantConcession';
import { TipoEquivalenciaEnum } from 'repository/timekeeper/@types/TypeEquivalentia';
import { TipoImpactoCambio } from 'repository/timekeeper/@types/TypeChangeImpact';
import { FrequencyUnitTime } from 'repository/timekeeper/@types/TypeFrequencyUnitTime';
import { DatePattern } from 'repository/timekeeper/@types/DatePattern';
import { Paycode } from 'repository/timekeeper/@types/PaycodeModel';
import { CalificadorCreate } from 'repository/timekeeper/@types/Calificador';
import { TipoProrrateoEnum } from 'repository/timekeeper/@types/TypePorrateo';
import { QualifiersList } from './QualifiersList';
import { TipoCantidadPeriodoDefinidoEnum } from 'repository/timekeeper/@types/TypeCantidadPeriodoDefinidoEnum';
import { TipoFechaAniversarioEnum } from 'repository/timekeeper/@types/TypeFechaAniversario';
import { GrantFijoCreateSchema } from 'schemas/Grants';

export interface GrantFormFijoData {
    grantConcession: GrantConcessionEnum;
    datePatternId: number | '';
    expiration: boolean;
    expirationNumber: number;
    expirationUnit: FrequencyUnitTime;
    fullTimeEquivalents: TipoEquivalenciaEnum;
    paycodeEtcId: number | '';
    impactChangeType: TipoImpactoCambio;
    impactRecalculate: boolean;
    qualifiers: CalificadorCreate[];
    apportionmentType: TipoProrrateoEnum | '';
}

interface GrantFormFijoProps {
    formData: GrantFormFijoData;
    setFormData: (data: GrantFormFijoData) => void;
    datePatterns: DatePattern[];
    paycodes: Paycode[];
}

export const GrantFormFijo: FC<GrantFormFijoProps> = ({
    formData,
    setFormData,
    datePatterns,
    paycodes
}) => {
    const { t } = useTranslation();

    // Estados de Prorrateo
    const [amountType, setAmountType] = React.useState<TipoCantidadPeriodoDefinidoEnum | ''>('');
    const [anniversaryType, setAnniversaryType] = React.useState<TipoFechaAniversarioEnum | ''>('');
    const [periodItems, setPeriodItems] = React.useState<Array<{ after: number; before: number; amount: number }>>([]);
    const [expirationError, setExpirationError] = React.useState<string | undefined>();

    // Seleccionar automáticamente el primer patrón de fechas
    useEffect(() => {
        if (datePatterns.length > 0 && formData.datePatternId === '') {
            const firstPattern = datePatterns[0];
            const firstPatternId = (firstPattern as any).date_pattern_id ?? (firstPattern as any).id;
            setFormData({ ...formData, datePatternId: firstPatternId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datePatterns, formData.datePatternId]);

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                <FormControl fullWidth>
                    {/* <InputLabel>{t('accruals.grants.dialog.fields.date_pattern')}</InputLabel>
                    <Select
                        value={formData.datePatternId}
                        onChange={(e) => setFormData({ ...formData, datePatternId: e.target.value as number })}
                        label={t('accruals.grants.dialog.fields.date_pattern')}
                    >
                        {datePatterns.map((pattern) => (
                            <MenuItem key={(pattern as any).date_pattern_id ?? (pattern as any).id} value={(pattern as any).date_pattern_id ?? (pattern as any).id}>
                                {(pattern as any).name}
                            </MenuItem>
                        ))}
                    </Select> */}
                    <Autocomplete
                        options={datePatterns}
                        getOptionLabel={(option) => (option as any).name}
                        value={datePatterns.find((pattern) => ((pattern as any).date_pattern_id ?? (pattern as any).id) === formData.datePatternId) || null}
                        onChange={(event, newValue) => {
                            setFormData({ ...formData, datePatternId: (newValue as any)?.date_pattern_id ?? (newValue as any)?.id ?? '' });
                        }}
                        renderInput={(params) => <TextField {...params} label={t('accruals.grants.dialog.fields.date_pattern')} />}
                    />
                </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {t('accruals.grants.dialog.fields.give_grant')}
                </Typography>
                <RadioGroup
                    value={formData.grantConcession}
                    onChange={(e) => setFormData({ ...formData, grantConcession: e.target.value as GrantConcessionEnum })}
                >
                    <FormControlLabel
                        value={GrantConcessionEnum.UNA_VEZ}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.one_time')}
                    />
                    <FormControlLabel
                        value={GrantConcessionEnum.REPETITIVO}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.repeatedly')}
                    />
                </RadioGroup>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {t('accruals.grants.dialog.fields.expiration')}
                </Typography>
                <RadioGroup
                    value={formData.expiration}
                    onChange={(e) => setFormData({ ...formData, expiration: e.target.value === 'true' })}
                >
                    <FormControlLabel
                        value={false}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.does_not_expire')}
                    />
                    <FormControlLabel
                        value={true}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.expires')}
                    />
                </RadioGroup>
            </Grid>

            {formData.expiration && (
                <Grid size={{ xs: 12 }} sx={{ mb: 1 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('accruals.grants.dialog.fields.expires')}
                                value={formData.expirationNumber}
                                onChange={(e) => {
                                    const value = Number(e.target.value);
                                    setFormData({ ...formData, expirationNumber: value });
                                    // Validar expiration_number
                                    const result = GrantFijoCreateSchema.shape.expiration_number.safeParse(value);
                                    setExpirationError(result.success ? undefined : result.error.issues[0]?.message);
                                }}
                                inputProps={{ min: 1, max: 100 }}
                                error={!!expirationError}
                                helperText={expirationError}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>{t('accruals.grants.dialog.fields.unit')}</InputLabel>
                                <Select
                                    value={formData.expirationUnit}
                                    onChange={(e) => setFormData({ ...formData, expirationUnit: e.target.value as FrequencyUnitTime })}
                                    label={t('accruals.grants.dialog.fields.unit')}
                                >
                                    <MenuItem value={FrequencyUnitTime.DIAS}>{t('accruals.grants.dialog.fields.unit_options.DAYS')}</MenuItem>
                                    <MenuItem value={FrequencyUnitTime.SEMANAS}>{t('accruals.grants.dialog.fields.unit_options.WEEKS')}</MenuItem>
                                    <MenuItem value={FrequencyUnitTime.MESES}>{t('accruals.grants.dialog.fields.unit_options.MONTHS')}</MenuItem>
                                    <MenuItem value={FrequencyUnitTime.ANOS}>{t('accruals.grants.dialog.fields.unit_options.YEARS')}</MenuItem>
                                    <MenuItem value={FrequencyUnitTime.PERIODO_PAGO}>{t('accruals.grants.dialog.fields.unit_options.PERIOD_PAY')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            )}

            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {t('accruals.grants.dialog.fields.full_time_equivalents')}
                </Typography>
                <RadioGroup
                    value={formData.fullTimeEquivalents}
                    onChange={(e) => setFormData({ ...formData, fullTimeEquivalents: e.target.value as TipoEquivalenciaEnum })}
                >
                    <FormControlLabel
                        value={TipoEquivalenciaEnum.NO_MODIFICA_CONCESION}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.do_not_modify_concession_according_to_ETC')}
                    />
                    <FormControlLabel
                        value={TipoEquivalenciaEnum.MODIFICA_CONCESION}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.modify_concession_according_to_ETC')}
                    />
                    <FormControlLabel
                        value={TipoEquivalenciaEnum.MODIFICA_CONCESION_PAYCODE}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.modify_concession_according_to_ETC_and_payment_code')}
                    />
                </RadioGroup>
            </Grid>

            {formData.fullTimeEquivalents === TipoEquivalenciaEnum.MODIFICA_CONCESION_PAYCODE && (
                <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                    <FormControl fullWidth>
                        {/* <InputLabel>{t('accruals.grants.dialog.fields.paycode')}</InputLabel>
                        <Select
                            value={formData.paycodeEtcId}
                            onChange={(e) => setFormData({ ...formData, paycodeEtcId: e.target.value as number })}
                            label={t('accruals.grants.dialog.fields.paycode')}
                        >
                            {paycodes.map((paycode) => (
                                <MenuItem key={paycode.paycode_id} value={paycode.paycode_id}>
                                    {paycode.name}
                                </MenuItem>
                            ))}
                        </Select> */}
                        <Autocomplete
                            options={paycodes}
                            getOptionLabel={(option) => option.name}
                            value={paycodes.find((pc) => pc.paycode_id === formData.paycodeEtcId) || null}
                            onChange={(event, newValue) => {
                                setFormData({ ...formData, paycodeEtcId: (newValue as any)?.paycode_id ?? '' });
                            }}
                            renderInput={(params) => <TextField {...params} label={t('accruals.grants.dialog.fields.paycode')} />}
                        />
                    </FormControl>
                </Grid>
            )}

            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {t('accruals.grants.dialog.fields.impact_of_the_change_in_ETC')}
                </Typography>
                <RadioGroup
                    value={formData.impactChangeType}
                    onChange={(e) => setFormData({ ...formData, impactChangeType: e.target.value as TipoImpactoCambio })}
                >
                    <FormControlLabel
                        value={TipoImpactoCambio.UTILIZAR_NORMA_ACUMULACION}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.use_accumulation_rule_parameters')}
                    />
                    <FormControlLabel
                        value={TipoImpactoCambio.VOLVER_CALCULAR_SALDO}
                        control={<Radio />}
                        label={t('accruals.grants.dialog.fields.recalculate_balance_when_changing_ETC')}
                    />
                </RadioGroup>
            </Grid>

            {/* Prorrateo */}
            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {t('accruals.grants.dialog.fields.apportionment')}
                </Typography>
                <FormControl fullWidth>
                    <InputLabel>{t('accruals.grants.dialog.fields.apportionment_type')}</InputLabel>
                    <Select
                        value={formData.apportionmentType}
                        onChange={(e) => setFormData({ ...formData, apportionmentType: e.target.value as TipoProrrateoEnum })}
                        label={t('accruals.grants.dialog.fields.apportionment_type')}
                    >
                        <MenuItem value="">{t('accruals.grants.dialog.fields.apportionment_type_options.none_apportionment')}</MenuItem>
                        <MenuItem value={TipoProrrateoEnum.FECHA_CONTRATACION}>{t('accruals.grants.dialog.fields.apportionment_type_options.based_on_the_date_of_hire')}</MenuItem>
                        <MenuItem value={TipoProrrateoEnum.FECHA_CONTRATACION_Y_FECHA_FIN_CONTRATO}>{t('accruals.grants.dialog.fields.apportionment_type_options.based_on_the_date_of_hire_and_the_contract_end_date')}</MenuItem>
                        <MenuItem value={TipoProrrateoEnum.FECHA_CONTRATACION_EN_PERIODOS_DEFINIDOS}>{t('accruals.grants.dialog.fields.apportionment_type_options.based_on_the_date_of_hire_in_user_defined_periods')}</MenuItem>
                        <MenuItem value={TipoProrrateoEnum.FECHA_ANIVERSARIO_CONTRATACION}>{t('accruals.grants.dialog.fields.apportionment_type_options.based_on_the_anniversary_date_of_hiring')}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            {formData.apportionmentType === TipoProrrateoEnum.FECHA_CONTRATACION_EN_PERIODOS_DEFINIDOS && (
                <>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>{t('accruals.grants.dialog.fields.amount_type')}</InputLabel>
                            <Select
                                value={amountType}
                                onChange={(e) => setAmountType(e.target.value as TipoCantidadPeriodoDefinidoEnum)}
                                label={t('accruals.grants.dialog.fields.amount_type')}
                            >
                                <MenuItem value={TipoCantidadPeriodoDefinidoEnum.HORAS}>{t('accruals.grants.dialog.fields.amount_type_options.hours_amount')}</MenuItem>
                                <MenuItem value={TipoCantidadPeriodoDefinidoEnum.DIAS}>{t('accruals.grants.dialog.fields.amount_type_options.days_amount')}</MenuItem>
                                <MenuItem value={TipoCantidadPeriodoDefinidoEnum.PORCENTAJE}>{t('accruals.grants.dialog.fields.amount_type_options.percentage_amount')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Listado de cantidades por período */}
                    <Grid size={{ xs: 12 }} sx={{ mt: 1 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="subtitle2">
                                {t('accruals.grants.dialog.fields.amount_per_period')}
                            </Typography>
                            <IconButton
                                onClick={() => setPeriodItems([...periodItems, { after: 0, before: 0, amount: 0 }])}
                                size="small"
                                color="primary"
                            >
                                <AddIcon />
                            </IconButton>
                        </Box>
                    </Grid>

                    {periodItems.map((item, idx) => (
                        <Grid size={{ xs: 12 }} key={idx} sx={{ mt: 1 }}>
                            <Box border={1} borderColor="grey.300" borderRadius={1} p={2} position="relative">
                                <IconButton
                                    onClick={() => setPeriodItems(periodItems.filter((_, i) => i !== idx))}
                                    size="small"
                                    sx={{ position: 'absolute', right: 8, top: 8 }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label={t('accruals.grants.dialog.fields.after_or_on')}
                                            value={item.after}
                                            onChange={(e) => {
                                                const v = Number(e.target.value);
                                                const updated = [...periodItems];
                                                updated[idx] = { ...updated[idx], after: v };
                                                setPeriodItems(updated);
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label={t('accruals.grants.dialog.fields.before_or_on')}
                                            value={item.before}
                                            onChange={(e) => {
                                                const v = Number(e.target.value);
                                                const updated = [...periodItems];
                                                updated[idx] = { ...updated[idx], before: v };
                                                setPeriodItems(updated);
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label={t('accruals.grants.dialog.fields.amount_of_concession')}
                                            value={item.amount}
                                            onChange={(e) => {
                                                const v = Number(e.target.value);
                                                const updated = [...periodItems];
                                                updated[idx] = { ...updated[idx], amount: v };
                                                setPeriodItems(updated);
                                            }}
                                            slotProps={{
                                                input: {
                                                    endAdornment: (
                                                    <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                                        {amountType === TipoCantidadPeriodoDefinidoEnum.HORAS && t('accruals.grants.dialog.fields.amount_of_concession_optiones.hours')}
                                                        {amountType === TipoCantidadPeriodoDefinidoEnum.DIAS && t('accruals.grants.dialog.fields.amount_of_concession_optiones.days')}
                                                        {amountType === TipoCantidadPeriodoDefinidoEnum.PORCENTAJE && t('accruals.grants.dialog.fields.amount_of_concession_optiones.percentage')}
                                                    </Box>
                                                )}
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    ))}
                </>
            )}

            {formData.apportionmentType === TipoProrrateoEnum.FECHA_ANIVERSARIO_CONTRATACION && (
                <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 1 }}>
                    <FormControl fullWidth>
                        <InputLabel>{t('accruals.grants.dialog.fields.anniversary_date')}</InputLabel>
                        <Select
                            defaultValue={TipoFechaAniversarioEnum.FECHA_ANIVERSARIO}
                            value={anniversaryType}
                            onChange={(e) => setAnniversaryType(e.target.value as TipoFechaAniversarioEnum)}
                            label={t('accruals.grants.dialog.fields.anniversary_date')}
                        >
                            <MenuItem value={TipoFechaAniversarioEnum.FECHA_ANIVERSARIO}>{t('accruals.grants.dialog.fields.anniversary_date_options.in_anniversary_date')}</MenuItem>
                            <MenuItem value={TipoFechaAniversarioEnum.ANTEA_FECHA_ANIVERSARIO}>{t('accruals.grants.dialog.fields.anniversary_date_options.before_anniversary_date')}</MenuItem>
                            <MenuItem value={TipoFechaAniversarioEnum.DESPUES_FECHA_ANIVERSARIO}>{t('accruals.grants.dialog.fields.anniversary_date_options.after_anniversary_date')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            )}

            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formData.impactRecalculate}
                            onChange={(e) => setFormData({ ...formData, impactRecalculate: e.target.checked })}
                        />
                    }
                    label={t('accruals.grants.dialog.fields.recalculate_balance_when_changing_accumulation_rule')}
                />
            </Grid>

            <QualifiersList
                qualifiers={formData.qualifiers}
                setQualifiers={(value) => setFormData({ ...formData, qualifiers: value })}
                paycodes={paycodes}
            />
        </Grid>
    );
};
